<template>
  <b-row>
    <b-col cols="12">
      <b-form novalidate>
        <b-card no-header>
          <template v-if="editing" #header>
            <b-row>
              <b-col cols="5">
                <strong>Usuário: {{ user.email }}</strong>
              </b-col>
              <b-col cols="4">
                <b-button
                  v-if="!user.confirmed"
                  size="sm"
                  @click="resendConfirmationEmail"
                  >Reenviar e-mail de confirmação
                </b-button>
              </b-col>
              <b-col cols="3" class="text-right">
                <b-form-checkbox
                  v-model="user.active"
                  class="mb-auto"
                  :disabled="viewOnly"
                  :value="true"
                  :unchecked-value="false"
                  >Usuário ativo?
                </b-form-checkbox>
              </b-col>
            </b-row>
          </template>

          <b-card-body>
            <!-- Informações do usuário (visíveis apenas na edição) -->
            <div v-if="editing" class="user-info-section mb-4">
              <b-row>
                <b-col cols="12" lg="4">
                  <div class="info-item">
                    <small class="text-muted d-block">Criado por</small>
                    <span>{{
                      user.createdBy
                        ? `${user.createdBy.name} ${user.createdBy.surname}`
                        : "-"
                    }}</span>
                  </div>
                </b-col>
                <b-col cols="12" lg="4">
                  <div class="info-item">
                    <small class="text-muted d-block">Data de Criação</small>
                    <span>{{ user.createdAt | datetime }}</span>
                  </div>
                </b-col>
                <b-col cols="12" lg="4">
                  <div class="info-item">
                    <small class="text-muted d-block">Primeiro Acesso</small>
                    <span>{{ user.firstLogin | datetime }}</span>
                  </div>
                </b-col>
              </b-row>
            </div>

            <b-row v-if="!editing">
              <b-col cols="12" lg="4">
                <b-form-group label="E-mail">
                  <b-form-input
                    v-model="user.email"
                    v-validate="rules.email"
                    type="email"
                    aria-describedby="emailInvalid"
                    name="email"
                    :state="validateState('email')"
                  ></b-form-input>
                  <b-form-invalid-feedback id="emailInvalid">
                    {{ errors.first("email") }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </b-col>
            </b-row>
            <b-row>
              <b-col cols="12" lg="6">
                <b-form-group label="Empresa">
                  <SearchableSelect
                    v-model="user.company"
                    :options="
                      companies.map((c) => ({
                        value: c._id,
                        text: c.fantasyName,
                      }))
                    "
                    :disabled="
                      !canSelectCompany || user.role === 'root' || viewOnly
                    "
                    placeholder="Selecione uma empresa"
                    search-placeholder="Buscar empresa…"
                    :use-company-filter="true"
                    :control-class="'form-control'"
                    @change="onCompanyChanged"
                  />
                </b-form-group>
              </b-col>
              <b-col cols="12" lg="6">
                <b-form-group label="Papel">
                  <b-form-select
                    v-model="user.role"
                    v-validate="rules.role"
                    class="form-control"
                    :options="rolesOpts"
                    aria-describedby="invalidRole"
                    name="role"
                    :disabled="!canEditRole"
                    :state="validateState('role')"
                  ></b-form-select>
                  <b-form-invalid-feedback id="invalidRole">
                    {{ errors.first("role") }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </b-col>
            </b-row>
            <b-row>
              <b-col cols="12" lg="4">
                <b-form-group label="Nome">
                  <b-form-input
                    v-model="user.name"
                    v-validate="rules.name"
                    type="text"
                    class="form-control"
                    aria-describedby="invalidName"
                    name="name"
                    :disabled="viewOnly"
                    :state="validateState('name')"
                  ></b-form-input>
                  <b-form-invalid-feedback id="invalidName">
                    {{ errors.first("name") }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </b-col>
              <b-col cols="12" lg="5">
                <b-form-group label="Sobrenome">
                  <b-form-input
                    v-model="user.surname"
                    v-validate="rules.surname"
                    type="text"
                    class="form-control"
                    aria-describedby="invalidSurname"
                    name="surname"
                    :disabled="viewOnly"
                    :state="validateState('surname')"
                  ></b-form-input>
                  <b-form-invalid-feedback id="invalidSurname">
                    {{ errors.first("surname") }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </b-col>
              <b-col cols="12" lg="3">
                <b-form-group label="CPF">
                  <b-form-input
                    ref="cpf"
                    v-model="user.cpf"
                    v-mask="'###.###.###-##'"
                    v-validate="rules.cpf"
                    type="text"
                    class="form-control"
                    aria-describedby="invalidCPF"
                    name="cpf"
                    :disabled="viewOnly"
                    :state="validateState('cpf')"
                  ></b-form-input>
                  <b-form-invalid-feedback id="invalidCPF">
                    {{ errors.first("cpf") }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </b-col>
            </b-row>
            <b-row>
              <b-col cols="12" lg="4">
                <b-form-group label="Telefone">
                  <b-form-input
                    ref="phone"
                    v-model="user.phone"
                    v-mask="['(##) ####-####', '(##) #####-####']"
                    v-validate="rules.phone"
                    type="tel"
                    class="form-control"
                    aria-describedby="invalidPhone"
                    name="phone"
                    :disabled="viewOnly"
                    :state="validateState('phone')"
                  ></b-form-input>
                  <b-form-invalid-feedback id="invalidPhone">
                    {{ errors.first("phone") }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </b-col>
              <b-col cols="12" lg="4">
                <b-form-group label="ID Interno">
                  <b-form-input
                    v-model="user.internalId"
                    type="text"
                    class="form-control"
                    :disabled="viewOnly"
                  ></b-form-input>
                </b-form-group>
              </b-col>
              <b-col cols="12" lg="4">
                <b-form-group label="Cargo">
                  <b-form-input
                    v-model="user.position"
                    type="text"
                    class="form-control"
                    :disabled="viewOnly"
                  ></b-form-input>
                </b-form-group>
              </b-col>
            </b-row>
            <b-row>
              <b-col cols="12" lg="6">
                <b-form-group label="Setor">
                  <b-form-select
                    v-model="user.department"
                    class="form-control"
                    :options="departmentsOpts"
                    :disabled="viewOnly"
                  ></b-form-select>
                </b-form-group>
              </b-col>
              <b-col cols="12" lg="6">
                <b-form-group label="Unidade">
                  <b-form-select
                    v-model="user.businessUnit"
                    class="form-control"
                    :options="businessUnitsOpts"
                    :disabled="viewOnly"
                  ></b-form-select>
                </b-form-group>
              </b-col>
            </b-row>
            <b-row>
              <b-col cols="12" lg="6">
                <b-form-group label="Endereço">
                  <b-form-input
                    v-model="user.address"
                    v-validate="rules.address"
                    type="text"
                    class="form-control"
                    aria-describedby="invalidAddress"
                    name="address"
                    :disabled="viewOnly"
                    :state="validateState('address')"
                  ></b-form-input>
                  <b-form-invalid-feedback id="invalidAddress">
                    {{ errors.first("address") }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </b-col>
              <b-col cols="12" lg="3">
                <b-form-group label="Estado">
                  <b-form-select
                    v-model="user.state"
                    v-validate="rules.state"
                    class="form-control"
                    :options="statesOpts"
                    aria-describedby="invalidState"
                    name="state"
                    :disabled="viewOnly"
                    :state="validateState('state')"
                    @change="refreshCities"
                  ></b-form-select>
                  <b-form-invalid-feedback id="invalidState">
                    {{ errors.first("state") }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </b-col>
              <b-col cols="12" lg="3">
                <b-form-group label="Cidade">
                  <b-form-select
                    v-model="user.city"
                    v-validate="rules.city"
                    class="form-control"
                    :options="citiesOpts"
                    aria-describedby="invalidCity"
                    name="city"
                    :disabled="viewOnly"
                    :state="validateState('city')"
                  ></b-form-select>
                  <b-form-invalid-feedback id="invalidCity">
                    {{ errors.first("city") }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </b-col>
            </b-row>
          </b-card-body>
          <template v-if="!viewOnly" #footer>
            <b-button
              variant="primary"
              class="px-4"
              :disabled="errors.any() || loading"
              @click="onSaveClicked"
              >Salvar
            </b-button>
            <b-button class="px-4 ml-1" :disabled="loading" @click="goBack"
              >Cancelar</b-button
            >
          </template>
        </b-card>
      </b-form>
    </b-col>
    <b-col cols="12" class="mt-3">
      <b-card
        v-if="editingSelf && profile.role === 'company-root'"
        no-body
        header="Aviso de Férias"
        class="mb-3"
      >
        <b-card-body>
          <b-alert
            v-if="showVacationSessionBanner"
            show
            variant="warning"
            class="mb-3"
          >
            Você está com aviso de férias ativo de
            <strong>{{ formatVacationDate(vacation.begin) }}</strong>
            até
            <strong>{{ formatVacationDate(vacation.end) }}</strong>
            <span v-if="designatedAdminName">
              . Administrador nomeado:
              <strong>{{ designatedAdminName }}</strong>
            </span>
          </b-alert>

          <b-row>
            <b-col cols="12" lg="6">
              <b-form-group label="Início">
                <b-form-datepicker
                  v-bind="vacationLocale.pt"
                  v-model="vacation.begin"
                  locale="pt"
                  :date-format-options="{
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                  }"
                  value-as-date
                  name="vacationBegin"
                  :min="`${minDate.getFullYear()}-${(minDate.getMonth() + 1)
                    .toString()
                    .padStart(2, '0')}-${minDate
                    .getDate()
                    .toString()
                    .padStart(2, '0')}`"
                  :state="validateVacationBegin()"
                />
              </b-form-group>
            </b-col>

            <b-col cols="12" lg="6">
              <b-form-group label="Fim">
                <b-form-datepicker
                  v-bind="vacationLocale.pt"
                  v-model="vacation.end"
                  locale="pt"
                  :date-format-options="{
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                  }"
                  value-as-date
                  name="vacationEnd"
                  :min="`${minDate.getFullYear()}-${(minDate.getMonth() + 1)
                    .toString()
                    .padStart(2, '0')}-${minDate
                    .getDate()
                    .toString()
                    .padStart(2, '0')}`"
                  :state="validateVacationEnd()"
                />
              </b-form-group>
            </b-col>
          </b-row>

          <b-row>
            <b-col cols="12" lg="6">
              <b-form-group
                label="Administrador nomeado"
                description="O usuário nomeado poderá ver as novas denúncias que chegarem nesse período"
              >
                <b-form-select
                  v-model="vacation.designatedAdmin"
                  :options="vacationUserOpts"
                ></b-form-select>
              </b-form-group>
            </b-col>
          </b-row>
        </b-card-body>
        <b-card-footer>
          <b-button
            :disabled="!(validateVacationBegin() && validateVacationEnd())"
            variant="primary"
            @click="saveVacation"
          >
            Salvar
          </b-button>
          <b-button
            class="ml-2"
            :disabled="!vacationConfigured"
            @click="resetVacation"
          >
            Cancelar Férias
          </b-button>
        </b-card-footer>
      </b-card>

      <b-card v-if="editingSelf" header="Autenticação">
        <b-row>
          <b-col>
            <cite
              >A autenticação de dois fatores é um recurso oferecido por vários
              prestadores de serviços online que acrescentam uma camada
              adicional de segurança para o processo de login da conta, exigindo
              que o usuário forneça duas formas de autenticação.
              <br />
              <small
                >Fonte<a
                  href="https://www.kaspersky.com.br/blog/o-que-e-a-autenticacao-de-dois-fatores-e-como-usa-la/3226/"
                  target="_blank"
                  rel="noopener"
                >
                  Kaspersky
                </a></small
              >
            </cite>
            <br />
            <br />
            Para habilitar a Autenticação de Dois Fatores (2FA) no
            SISCompliance, utilize um app confiável que suporte
            <em>One Time Password (OTP)</em>
            e digite o token logo abaixo para ativar na sua conta.
          </b-col>
        </b-row>
        <hr />
        <b-row v-if="!loading" class="text-center">
          <b-col>
            Autenticação em dois fatores
            <span v-if="user.tfa.enabled" class="text-success">habilitada</span>
            <span v-else class="text-danger"> desabilitada</span>
          </b-col>
        </b-row>
        <b-row v-if="!loading && !user.tfa.enabled">
          <b-col>
            <b-row class="text-center">
              <b-col cols="12">
                <b-form-group
                  description="Utilize um dos apps abaixo ou outro de sua preferência compatível com OTP
                                para ler o QRCode e habilitar sua conta"
                >
                  <b-img
                    src="/api/users/tfa-token"
                    alt="Two Factor Authentication QR Code"
                  ></b-img>
                </b-form-group>
                <b-row class="text-center">
                  <b-col>
                    <b-button v-b-toggle.ms-auth variant="outline-primary"
                      >Microsoft Authenticator</b-button
                    >
                    <b-collapse id="ms-auth" class="mt-2">
                      <a
                        href="https://play.google.com/store/apps/details?id=com.azure.authenticator"
                        target="_blank"
                        rel="noopener"
                      >
                        <b-img src="img/google-play.svg" />
                      </a>
                      <a
                        href="https://itunes.apple.com/app/microsoft-authenticator/id983156458"
                        target="_blank"
                        rel="noopener"
                      >
                        <b-img src="img/app-store.svg" />
                      </a>
                    </b-collapse>
                  </b-col>
                </b-row>
                <b-row class="text-center my-2">
                  <b-col>
                    <b-button v-b-toggle.authy variant="outline-primary"
                      >Authy</b-button
                    >
                    <b-collapse id="authy" class="mt-2">
                      <a
                        href="https://play.google.com/store/apps/details?id=com.authy.authy"
                        target="_blank"
                        rel="noopener"
                      >
                        <b-img src="img/google-play.svg" />
                      </a>
                      <a
                        href="https://itunes.apple.com/us/app/authy/id494168017"
                        target="_blank"
                        rel="noopener"
                      >
                        <b-img src="img/app-store.svg" />
                      </a>
                    </b-collapse>
                  </b-col>
                </b-row>
              </b-col>
            </b-row>
            <b-row class="mt-2">
              <b-col cols="6" offset="3">
                <b-form-group description="Digite o token para confirmar">
                  <b-form-input
                    v-model="token"
                    type="text"
                    class="form-control"
                    name="token"
                    maxlength="6"
                    @input="validateToken()"
                    @keydown.enter.prevent
                  ></b-form-input>
                </b-form-group>
              </b-col>
            </b-row>
          </b-col>
        </b-row>
        <b-row
          v-else-if="!loading && user.tfa.enabled"
          class="text-center mt-2"
        >
          <b-col>
            <b-button variant="danger" @click="disableToken()"
              >Desabilitar 2FA</b-button
            >
          </b-col>
        </b-row>
        <b-row v-else class="text-center">
          <b-col> Carregando... </b-col>
        </b-row>
      </b-card>
      <b-card v-else header="Autenticação">
        <b-row>
          <b-col>
            <cite
              >A autenticação de dois fatores é um recurso oferecido por vários
              prestadores de serviços online que acrescentam uma camada
              adicional de segurança para o processo de login da conta, exigindo
              que o usuário forneça duas formas de autenticação.
              <br />
              <small
                >Fonte<a
                  href="https://www.kaspersky.com.br/blog/o-que-e-a-autenticacao-de-dois-fatores-e-como-usa-la/3226/"
                  target="_blank"
                  rel="noopener"
                >
                  Kaspersky
                </a></small
              >
            </cite>
            <br />
            <br />
          </b-col>
        </b-row>
        <hr />
        <b-row v-if="!loading" class="text-center">
          <b-col>
            Autenticação em dois fatores
            <span v-if="user.tfa.enabled" class="text-success">habilitada</span>
            <span v-else class="text-danger"> desabilitada</span>
          </b-col>
        </b-row>
        <b-row v-if="user.tfa.enabled" class="text-center mt-2">
          <b-col>
            <b-button variant="danger" @click="disableToken()"
              >Desabilitar 2FA</b-button
            >
          </b-col>
        </b-row>
      </b-card>
    </b-col>
  </b-row>
</template>

<script>
import {
  getUserInfo,
  editUser,
  newUser,
  resendConfirmation,
  confirmToken,
  removeTFA,
  getVacation,
  setVacation,
  cancelVacation,
} from "../../api/user";
import { getStates, getCities } from "../../api/geo";
import {
  getCompanies,
  getCompanyRoles,
  getClassificationValues,
  getCompanyUsers,
} from "../../api/company";
import { mapGetters } from "vuex";

import { mask } from "vue-the-mask";
import SearchableSelect from "../../components/SearchableSelect.vue";

export default {
  name: "UserView",
  components: { SearchableSelect },
  directives: { mask },
  props: {
    id: {
      type: [String, Number],
      default: "perfil",
    },
  },
  data() {
    return {
      loading: false,
      user: { confirmed: true, tfa: {} },
      token: "",
      rules: {
        email: {
          required: true,
          email: true,
          "email-availability": true,
        },
        name: {
          required: true,
        },
        surname: {
          required: true,
        },
        cpf: {
          cpf: true, // Validação apenas se preenchido
        },
        phone: {
          // required: true, // Removido obrigatoriedade
        },
        address: {
          // required: true, // Removido obrigatoriedade
        },
        city: {
          // required: true, // Removido obrigatoriedade
        },
        state: {
          // required: true, // Removido obrigatoriedade
        },
        role: {
          required: true,
        },
        internalId: {
          // required: false,
        },
        position: {
          // required: false,
        },
        department: {
          // required: false,
        },
        businessUnit: {
          // required: false,
        },
      },
      editing: false,
      states: [],
      cities: [],
      companies: [],
      roles: [],
      departments: [],
      businessUnits: [],
      companyUser: true,
      userRole: null,
      vacation: {
        begin: null,
        end: null,
        designatedAdmin: null,
      },
      vacationConfigured: false,
      companyUsers: [],
      vacationUserOpts: [],
      showVacationSessionBanner: false,
      vacationLocale: {
        pt: {
          labelPrevDecade: "Década anterior",
          labelPrevYear: "Ano anterior",
          labelPrevMonth: "Mês anterior",
          labelCurrentMonth: "Mês atual",
          labelNextMonth: "Próximo mês",
          labelNextYear: "Próximo ano",
          labelNextDecade: "Próxima década",
          labelTodayButton: "Hoje",
          labelSelected: "Data selecionada",
          labelNoDateSelected: "Selecione uma data",
          labelCalendar: "Calendário",
          labelNav: "Navegação do calendário",
          labelHelp: "Use as setas para navegar",
        },
      },
    };
  },
  computed: {
    ...mapGetters(["profile", "permissions"]),
    minDate() {
      return new Date();
    },
    designatedAdminName() {
      if (!this.vacation.designatedAdmin || !this.companyUsers.length) {
        return "";
      }

      const designated = this.companyUsers.find(
        (companyUser) => companyUser._id === this.vacation.designatedAdmin
      );

      if (!designated) {
        return "";
      }

      return `${designated.name} ${designated.surname}`.trim();
    },
    editingSelf() {
      return this.id === "perfil" || this.id == this.profile._id;
    },

    isCompanyRootWithManagedCompanies() {
      return (
        this.profile.role === "company-root" &&
        this.profile.managedCompanies?.length > 0
      );
    },

    canSelectCompany() {
      // Root pode sempre selecionar empresa
      if (this.profile.role === "root") {
        return true;
      }
      // Company-root com empresas gerenciadas pode selecionar ao criar novo usuário
      if (this.isCompanyRootWithManagedCompanies && !this.editing) {
        return true;
      }
      return false;
    },

    canChange() {
      const role = this.profile.role;
      if (parseInt(role) === role) {
        const permissions = this.permissions;
        return (
          (permissions.user && permissions.user.create) || permissions.user.edit
        );
      }
      return true;
    },

    viewOnly() {
      return !this.canChange && !this.editingSelf;
    },

    canEditRole() {
      if (!this.editing) {
        return true;
      }
      if (this.profile.role === "root") {
        return true;
      }
      if (this.profile.role === "company-root") {
        return (
          this.user.company === this.profile.company ||
          this.profile.managedCompanies?.includes(this.user.company)
        );
      }
      if (this.viewOnly) {
        return false;
      }
      return (
        !this.editingSelf &&
        this.user.role &&
        ["root", "company-root"].indexOf(this.user.role) === -1
      );
    },

    companiesOpts() {
      const opts = [];
      if (this.profile.role === "root") {
        opts.push({ value: null, text: "" });
      }
      for (const company of this.companies) {
        opts.push({ value: company._id, text: company.fantasyName });
      }
      return opts;
    },
    statesOpts() {
      const opts = [];
      opts.push({
        value: undefined,
        text: "Por favor selecione um estado",
        disabled: true,
      });
      for (const state of this.states) {
        opts.push({ value: state._id, text: state.name });
      }
      return opts;
    },
    citiesOpts() {
      const opts = [];
      opts.push({
        value: undefined,
        text: "Por favor selecione uma cidade",
        disabled: true,
      });
      for (const city of this.cities) {
        opts.push({ value: city.code, text: city.name });
      }
      return opts;
    },
    rolesOpts() {
      const opts = [];
      for (const role of this.roles) {
        const opt = { value: role._id, text: role.name };
        if (
          this.profile.role !== "root" &&
          role._id === "company-root" &&
          !this.editingSelf
        ) {
          opt.disabled = true;
        }
        opts.push(opt);
      }
      if (
        this.userRole &&
        !this.roles.some((r) => r._id === this.userRole._id)
      ) {
        opts.unshift({ value: this.userRole._id, text: this.userRole.name });
      }
      opts.unshift({
        value: undefined,
        text: "Por favor selecione um papel",
        disabled: true,
      });
      return opts;
    },
    departmentsOpts() {
      const opts = [];
      opts.push({
        value: undefined,
        text: "Por favor selecione um setor",
        disabled: false,
      });
      for (const dept of this.departments) {
        opts.push({ value: parseInt(dept._id), text: dept.value });
      }
      return opts;
    },
    businessUnitsOpts() {
      const opts = [];
      opts.push({
        value: undefined,
        text: "Por favor selecione uma unidade",
        disabled: false,
      });
      for (const unit of this.businessUnits) {
        opts.push({ value: parseInt(unit._id), text: unit.value });
      }
      return opts;
    },
  },

  async created() {
    this.loading = true;
    await this.loadStates();
    let id = this.id;

    if (this.id === "perfil") {
      id = this.profile._id;
    }
    const iamGRoot = this.profile.role === "root";
    const iamCompanyRootWithManagedCompanies =
      this.profile.role === "company-root" &&
      this.profile.managedCompanies?.length > 0;

    this.editing = parseInt(id) == id;
    this.companyUser = !iamGRoot && !iamCompanyRootWithManagedCompanies;

    await this.loadCompanies();

    if (this.editing) {
      const response = await getUserInfo(id);
      const user = response.data;

      if (user.state) {
        this.loadCities(user.state);
      }

      await this.loadRoles(user.company);
      await this.loadDepartments(user.company);
      await this.loadBusinessUnits(user.company);

      user.tfa = user.tfa || { enabled: false };
      this.user = user;
      this.loading = false;

      const updateEls = ["cpf", "phone"];
      this.$nextTick(() => {
        for (const id of updateEls) {
          this.$refs[id].$el.oninput({ isTrusted: true });
        }
      });
    } else {
      // Para company-root com empresas gerenciadas, não pré-selecionar empresa
      // para permitir escolha da empresa destino
      if (iamCompanyRootWithManagedCompanies) {
        // Carregar roles da própria empresa inicialmente
        await this.loadRoles(this.profile.company);
        await this.loadDepartments(this.profile.company);
        await this.loadBusinessUnits(this.profile.company);
        this.loading = false;
        // Não pré-seleciona empresa, deixa o usuário escolher
      } else {
        await this.loadRoles(this.profile.company);
        await this.loadDepartments(this.profile.company);
        await this.loadBusinessUnits(this.profile.company);

        this.loading = false;
        if (this.companyUser) {
          this.companyUser = false;
          this.$nextTick(() => {
            this.user.company = this.profile.company;
            this.companyUser = true;
          });
        }
      }
    }

    if (this.editingSelf && this.profile.role === "company-root") {
      await this.loadVacation();
      await this.loadVacationUserOpts();
      this.evaluateVacationSessionNotice();
    }
  },
  methods: {
    formatVacationDate(value) {
      if (!value) {
        return "";
      }

      const date = value instanceof Date ? value : new Date(value);
      if (Number.isNaN(date.getTime())) {
        return "";
      }

      return date.toLocaleDateString("pt-BR");
    },
    validateVacationBegin() {
      if (this.vacation.begin) {
        if (this.vacation.end) {
          if (this.vacation.begin.getTime() > this.vacation.end.getTime()) {
            return false;
          }
        }
        return true;
      }
    },
    validateVacationEnd() {
      if (this.vacation.end) {
        return true;
      }
    },
    isVacationActive() {
      if (!this.vacation.begin || !this.vacation.end) {
        return false;
      }

      const begin = new Date(this.vacation.begin);
      const end = new Date(this.vacation.end);

      if (Number.isNaN(begin.getTime()) || Number.isNaN(end.getTime())) {
        return false;
      }

      const now = new Date();
      return begin <= now && now <= end;
    },
    getVacationSessionNoticeKey() {
      if (!this.profile || !this.profile._id || !this.vacation.end) {
        return "";
      }

      const end = new Date(this.vacation.end);
      const begin = new Date(this.vacation.begin);

      if (Number.isNaN(end.getTime()) || Number.isNaN(begin.getTime())) {
        return "";
      }

      return `vacation-notice:${
        this.profile._id
      }:${begin.toISOString()}:${end.toISOString()}`;
    },
    evaluateVacationSessionNotice() {
      this.showVacationSessionBanner = false;

      if (!this.isVacationActive()) {
        return;
      }

      const key = this.getVacationSessionNoticeKey();
      if (!key) {
        return;
      }

      try {
        const alreadyShown = window.sessionStorage.getItem(key);
        if (alreadyShown) {
          return;
        }
        this.showVacationSessionBanner = true;
        window.sessionStorage.setItem(key, "1");
      } catch (_) {
        this.showVacationSessionBanner = true;
      }
    },
    async loadVacation() {
      const response = await getVacation();
      const vacationData = response.data;

      if (vacationData && vacationData.begin) {
        this.vacation.begin = new Date(vacationData.begin);
        this.vacation.end = new Date(vacationData.end);
        this.vacation.designatedAdmin = vacationData.designatedAdmin;
        this.vacationConfigured = true;
      }
    },
    async loadVacationUserOpts() {
      const response = await getCompanyUsers(this.profile.company);
      this.companyUsers = response.data;
      const userOpts = [];

      const myId = this.profile._id;
      for (const companyUser of this.companyUsers) {
        if (companyUser._id === myId) {
          continue;
        }
        userOpts.push({
          value: companyUser._id,
          text: `${companyUser.name} ${companyUser.surname}`,
        });
      }
      userOpts.unshift({ value: null, text: "" });
      this.vacationUserOpts = userOpts;
    },
    async resetVacation() {
      const currentNoticeKey = this.getVacationSessionNoticeKey();
      this.vacation = {
        begin: null,
        end: null,
        designatedAdmin: null,
      };
      this.showVacationSessionBanner = false;

      if (currentNoticeKey) {
        try {
          window.sessionStorage.removeItem(currentNoticeKey);
        } catch (_) {
          // Ignora indisponibilidade de sessionStorage
        }
      }

      try {
        await cancelVacation();
        this.$bvToast.toast("Férias canceladas com sucesso!", {
          title: "Sucesso",
          toastClass: "custom-toast custom-toast-success",
          variant: "light",
          solid: true,
          autoHideDelay: 5000,
          noCloseButton: false,
        });
      } catch (error) {
        if (error.response.status >= 400 && error.response.status < 500) {
          this.$bvToast.toast(error.response.data, {
            title: "Erro",
            toastClass: "custom-toast custom-toast-error",
            variant: "light",
            solid: true,
            autoHideDelay: 10000,
            noCloseButton: false,
          });
        } else {
          this.$bvToast.toast(error.response.statusText, {
            title: "Erro",
            toastClass: "custom-toast custom-toast-error",
            variant: "light",
            solid: true,
            autoHideDelay: 10000,
            noCloseButton: false,
          });
        }
      }

      this.vacationConfigured = false;
    },
    async saveVacation() {
      try {
        await setVacation(this.vacation);
        this.vacationConfigured = true;
        this.evaluateVacationSessionNotice();
        this.$bvToast.toast("Aviso de Férias configuradas com sucesso!", {
          title: "Sucesso",
          toastClass: "custom-toast custom-toast-success",
          variant: "light",
          solid: true,
          autoHideDelay: 5000,
          noCloseButton: false,
        });
      } catch (error) {
        if (error.response.status >= 400 && error.response.status < 500) {
          this.$bvToast.toast(error.response.data, {
            title: "Erro",
            toastClass: "custom-toast custom-toast-error",
            variant: "light",
            solid: true,
            autoHideDelay: 10000,
            noCloseButton: false,
          });
        } else {
          this.$bvToast.toast(error.response.statusText, {
            title: "Erro",
            toastClass: "custom-toast custom-toast-error",
            variant: "light",
            solid: true,
            autoHideDelay: 10000,
            noCloseButton: false,
          });
        }
      }
    },
    validateState(ref) {
      if (
        this.vFields[ref] &&
        (this.vFields[ref].dirty || this.vFields[ref].validated)
      ) {
        return !this.errors.has(ref);
      }
      return null;
    },

    goBack() {
      this.$emit("cancelled");
      if (!this.$listeners.cancelled) {
        this.$router.go(-1);
      }
    },

    async validateToken() {
      if (this.token.length < 6) {
        return;
      }
      try {
        await confirmToken(this.token);
        this.$bvToast.toast("2FA habilitado com sucesso!", {
          title: "Sucesso",
          toastClass: "custom-toast custom-toast-success",
          variant: "light",
          solid: true,
          autoHideDelay: 5000,
          noCloseButton: false,
        });
        this.user.tfa.enabled = true;
        this.token = "";
      } catch (e) {
        this.$bvToast.toast("Token inválido", {
          title: "Erro",
          toastClass: "custom-toast custom-toast-error",
          variant: "light",
          solid: true,
          autoHideDelay: 10000,
          noCloseButton: false,
        });
      }
    },

    async disableToken() {
      try {
        if (this.editingSelf) {
          await removeTFA();
        } else {
          await removeTFA(this.user);
        }
        this.$bvToast.toast("2FA desabilitado com sucesso", {
          title: "Sucesso",
          toastClass: "custom-toast custom-toast-success",
          variant: "light",
          solid: true,
          autoHideDelay: 5000,
          noCloseButton: false,
        });
        this.user.tfa.enabled = false;
        this.token = "";
      } catch (e) {
        this.$bvToast.toast("Erro ao desabilitar 2FA", {
          title: "Erro",
          toastClass: "custom-toast custom-toast-error",
          variant: "light",
          solid: true,
          autoHideDelay: 10000,
          noCloseButton: false,
        });
      }
    },

    async onSaveClicked() {
      const validated = await this.$validator.validateAll();
      if (!validated) return;

      // Validar se empresa foi selecionada (obrigatório para company-root criando usuários)
      if (
        !this.editing &&
        this.isCompanyRootWithManagedCompanies &&
        !this.user.company
      ) {
        this.$bvToast.toast(
          "Por favor, selecione uma empresa para o usuário.",
          {
            title: "Atenção",
            toastClass: "custom-toast custom-toast-warning",
            variant: "light",
            solid: true,
            autoHideDelay: 5000,
            noCloseButton: false,
          }
        );
        return;
      }

      const payload = JSON.parse(JSON.stringify(this.user));

      if (payload.phone) {
        payload.phone = payload.phone.replace(/[^\d]/g, "");
      }
      if (payload.cpf) {
        payload.cpf = payload.cpf.replace(/[^\d]/g, "");
      }

      this.loading = true;

      let action = this.editing ? editUser : newUser;

      const response = await action(payload);
      this.loading = false;

      this.$bvToast.toast(
        `Usuário ${this.editing ? "editado" : "criado"} com sucesso!`,
        {
          title: "Sucesso",
          toastClass: "custom-toast custom-toast-success",
          variant: "light",
          solid: true,
          autoHideDelay: 5000,
          noCloseButton: false,
        }
      );

      // Se for criação de company-root e tiver URL de prévia, abrir em nova janela
      if (
        !this.editing &&
        response &&
        response.data &&
        response.data.emailPreviewUrl
      ) {
        const previewUrl = response.data.emailPreviewUrl;
        console.log(
          "🔍 [Frontend] Abrindo prévia do email de credenciais EAD em nova janela:",
          previewUrl
        );
        window.open(
          previewUrl,
          "_blank",
          "width=800,height=600,scrollbars=yes,resizable=yes"
        );
      }

      this.$emit("saved");
      if (!this.$listeners.saved) {
        this.goBack(-1);
      }
    },
    async loadCompanies() {
      const response = await getCompanies();
      this.companies = response.data;
    },
    async loadStates() {
      const response = await getStates();
      this.states = response.data;
    },
    async loadCities(state) {
      const response = await getCities(state);
      this.cities = response.data;
    },
    async refreshCities(state) {
      this.user.city = undefined;
      await this.loadCities(state);
    },
    async loadRoles(company) {
      let roles = [];
      if (company) {
        const response = await getCompanyRoles(company);
        roles.push(...response.data);
      }

      const companyRoot = {
        _id: "company-root",
        name: "Master da Empresa",
        disabled: true,
      };
      roles.unshift(companyRoot);

      if (
        this.profile.role === "company-root" ||
        this.profile.role === "root"
      ) {
        companyRoot.disabled = false;
      }
      if (this.profile.role === "root" && !company) {
        roles.unshift({ _id: "root", name: "Master Conformità" });
      }
      this.roles = roles;
    },
    async loadDepartments(company) {
      if (company) {
        try {
          const response = await getClassificationValues(company, 7); // INVOLVED_DEPARTMENT
          this.departments = response.data;
        } catch (error) {
          console.warn("Erro ao carregar setores:", error);
          this.departments = [];
        }
      } else {
        this.departments = [];
      }
    },
    async loadBusinessUnits(company) {
      if (company) {
        try {
          const response = await getClassificationValues(company, 8); // BUSINESS_UNIT
          this.businessUnits = response.data;
        } catch (error) {
          console.warn("Erro ao carregar unidades:", error);
          this.businessUnits = [];
        }
      } else {
        this.businessUnits = [];
      }
    },

    onCompanyChanged(companyId) {
      // Reset dependent fields and reload data when company changes
      this.user.role = null;
      this.user.department = null;
      this.user.businessUnit = null;
      this.loadRoles(companyId);
      this.loadDepartments(companyId);
      this.loadBusinessUnits(companyId);
    },

    async resendConfirmationEmail() {
      try {
        await resendConfirmation(this.user._id);
        this.$bvToast.toast("E-mail de confirmação com sucesso!", {
          title: "Sucesso",
          toastClass: "custom-toast custom-toast-success",
          variant: "light",
          solid: true,
          autoHideDelay: 5000,
          noCloseButton: false,
        });
      } catch (error) {
        if (error.response.status === 403 || error.response.status === 404) {
          this.$bvToast.toast(error.response.data, {
            title: "Erro",
            toastClass: "custom-toast custom-toast-error",
            variant: "light",
            solid: true,
            autoHideDelay: 10000,
            noCloseButton: false,
          });
        } else {
          this.$bvToast.toast(error.response.statusText, {
            title: "Erro",
            toastClass: "custom-toast custom-toast-error",
            variant: "light",
            solid: true,
            autoHideDelay: 10000,
            noCloseButton: false,
          });
        }
      }
    },
  },
};
</script>

<style scoped>
.user-info-section {
  background-color: #f8f9fa;
  border-radius: 8px;
  padding: 1rem;
  border: 1px solid #e9ecef;
}

.info-item {
  margin-bottom: 0.5rem;
}

.info-item small {
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
}

.info-item span {
  font-size: 0.9rem;
  color: #495057;
}
</style>
